import React, { FC, lazy, Suspense } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { PageContext } from './components/PageContext'
import LegalNotice from './components/LegalNotice'
import Footer from './components/Footer'
import { ThemeProvider } from 'styled-components'
import './App.css'
import { useConfig } from './hooks/useConfig'
import { useDomain } from './hooks/useDomain'
import { usePageMeta } from './hooks/usePageMeta'
import Home from './components/Home'
import RevealGame from './components/RevealGame'
import VstDownload from './customise/VstDownload'
import CookieBanner from './components/CookieBanner'
import {
    CookieConsentProvider,
    useCookieConsentContext,
} from './components/CookieConsentContext'
import { useMetaPixel } from './hooks/useMetaPixel'

const Release = lazy(() => import('./components/Release'))
const Poster = lazy(() => import('./components/Poster'))

/** Inner component — has access to CookieConsentContext */
const AppInner: FC = () => {
    const domain = useDomain()
    const { config, theme, loading, error } = useConfig(domain)
    const { analyticsAllowed, showBanner, accept, reject } =
        useCookieConsentContext()
    usePageMeta(config && config?.name, null)
    useMetaPixel(analyticsAllowed)

    if (loading) {
        return <div className="App">Loading label page...</div>
    }

    return (
        <PageContext.Provider value={config}>
            <div
                className="App"
                style={{
                    position: 'relative',
                    color: theme.text,
                    zIndex: 1,
                }}
            >
                <ThemeProvider theme={theme}>
                    <Suspense fallback={<div>Loading...</div>}>
                        <Router>
                            {error && (
                                <div
                                    style={{
                                        marginBottom: '12px',
                                        color: theme.secondaryText,
                                        fontSize: '0.8rem',
                                    }}
                                >
                                    {
                                        'Content backend unavailable. Showing fallback label profile.'
                                    }
                                </div>
                            )}
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route
                                    path="/release/:id"
                                    element={<Release />}
                                />
                                <Route
                                    path="/release/:id/revealgame"
                                    element={<RevealGame />}
                                />
                                <Route
                                    path="/impressum"
                                    element={<LegalNotice />}
                                />
                                <Route
                                    path="/release/:id/poster"
                                    element={<Poster />}
                                />
                                <Route
                                    path="/free-vst"
                                    element={<VstDownload />}
                                />
                            </Routes>
                            <Footer />
                        </Router>
                    </Suspense>
                </ThemeProvider>
                {showBanner && (
                    <CookieBanner onAccept={accept} onReject={reject} />
                )}
            </div>
        </PageContext.Provider>
    )
}

const App: FC = () => (
    <CookieConsentProvider>
        <AppInner />
    </CookieConsentProvider>
)

export default App
